import React, { useState, useEffect, useMemo } from 'react';
import { useTestMasters } from '../../hooks/useTestMasters';
import { FaTimes, FaPlus, FaTrash } from 'react-icons/fa';

const HEALTH_SCORE_CATEGORIES = [
  { name: 'Metabolic Health', color: 'bg-blue-100', textColor: 'text-blue-700', buttonColor: 'bg-blue-600 hover:bg-blue-700', headerColor: 'bg-blue-600' },
  { name: 'Heart Health', color: 'bg-red-100', textColor: 'text-red-700', buttonColor: 'bg-red-600 hover:bg-red-700', headerColor: 'bg-red-600' },
  { name: 'Cognition and Memory', color: 'bg-purple-100', textColor: 'text-purple-700', buttonColor: 'bg-purple-600 hover:bg-purple-700', headerColor: 'bg-purple-600' },
  { name: 'Endurance and Fitness', color: 'bg-green-100', textColor: 'text-green-700', buttonColor: 'bg-green-600 hover:bg-green-700', headerColor: 'bg-green-600' },
  { name: 'Immune Health', color: 'bg-yellow-100', textColor: 'text-yellow-700', buttonColor: 'bg-yellow-600 hover:bg-yellow-700', headerColor: 'bg-yellow-600' },
  { name: 'Gut Health', color: 'bg-orange-100', textColor: 'text-orange-700', buttonColor: 'bg-orange-600 hover:bg-orange-700', headerColor: 'bg-orange-600' },
  { name: 'Inflammation', color: 'bg-pink-100', textColor: 'text-pink-700', buttonColor: 'bg-pink-600 hover:bg-pink-700', headerColor: 'bg-pink-600' },
  { name: 'Bone Health', color: 'bg-indigo-100', textColor: 'text-indigo-700', buttonColor: 'bg-indigo-600 hover:bg-indigo-700', headerColor: 'bg-indigo-600' },
  { name: 'Female Hormones', color: 'bg-rose-100', textColor: 'text-rose-700', buttonColor: 'bg-rose-600 hover:bg-rose-700', headerColor: 'bg-rose-600' },
  { name: 'Male Hormones', color: 'bg-cyan-100', textColor: 'text-cyan-700', buttonColor: 'bg-cyan-600 hover:bg-cyan-700', headerColor: 'bg-cyan-600' },
  { name: 'Sleep and Recovery', color: 'bg-teal-100', textColor: 'text-teal-700', buttonColor: 'bg-teal-600 hover:bg-teal-700', headerColor: 'bg-teal-600' }
];

const CategoryScoreModal = () => {
  const { allTestMasters, updateTestMaster, loading } = useTestMasters();
  const [isOpen, setIsOpen] = useState(false);
  const [modalMode, setModalMode] = useState('edit'); // 'edit' or 'add'
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [weightages, setWeightages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedIndex, setExpandedIndex] = useState(null);
  
  // Get category config
  const categoryConfig = useMemo(() => {
    return HEALTH_SCORE_CATEGORIES.find(cat => cat.name === selectedCategory) || HEALTH_SCORE_CATEGORIES[0];
  }, [selectedCategory]);

  // Open modal for editing a test's weightage
  const openEditModal = (test, category) => {
    setSelectedTest(test.fullTestData);
    setSelectedCategory(category);
    const weightagesData = (test.fullTestData.health_score_weightage || []).map(item => ({
      ...item,
      associated_test_types: item.associated_test_types || ['PRO']
    }));
    setWeightages(weightagesData);
    // Find the index of the selected category and expand it
    const selectedCategoryIndex = weightagesData.findIndex(item => item.name === category);
    setExpandedIndex(selectedCategoryIndex >= 0 ? selectedCategoryIndex : null);
    setModalMode('edit');
    setIsOpen(true);
  };

  // Open modal for adding a new test to a category
  const openAddModal = (category) => {
    setSelectedTest(null);
    setSelectedCategory(category);
    setWeightages([]); // Will be set when test is selected
    setModalMode('add');
    setSearchTerm('');
    setIsOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsOpen(false);
    setSelectedTest(null);
    setSelectedCategory('');
    setWeightages([]);
    setSearchTerm('');
  };

  // Filter tests for the "add" mode - unique by test_code
  const filteredTests = useMemo(() => {
    if (modalMode !== 'add' || !searchTerm) return [];
    
    const term = searchTerm.toLowerCase();
    const filtered = allTestMasters.filter(test => 
      (test.biomarker_name && test.biomarker_name.toLowerCase().includes(term)) ||
      (test.test_code && test.test_code.toLowerCase().includes(term))
    );
    
    // Remove duplicates by test_code
    const uniqueTests = {};
    filtered.forEach(test => {
      if (!uniqueTests[test.test_code]) {
        uniqueTests[test.test_code] = test;
      }
    });
    
    return Object.values(uniqueTests);
  }, [allTestMasters, searchTerm, modalMode]);

  // Handle weight change for a category
  const handleWeightChange = (categoryName, newWeight) => {
    // Allow empty string or valid numbers
    const value = newWeight === '' ? '' : parseInt(newWeight) || 0;
    
    setWeightages(prev => {
      const existing = prev.find(w => w.name === categoryName);
      if (existing) {
        return prev.map(w => 
          w.name === categoryName ? { ...w, weight: value } : w
        );
      } else {
        return [...prev, { name: categoryName, weight: value }];
      }
    });
  };

  // Remove a category weightage
  const removeWeightage = (categoryName) => {
    setWeightages(prev => prev.filter(w => w.name !== categoryName));
  };

  // Add a new category weightage
  const addWeightage = () => {
    const availableCategories = HEALTH_SCORE_CATEGORIES.filter(
      cat => !weightages.find(w => w.name === cat.name)
    );
    
    if (availableCategories.length > 0) {
      setWeightages(prev => [...prev, { 
        name: availableCategories[0].name, 
        weight: 0,
        score_levels: {
          optimal: 100,
          normal: 80,
          suboptimal: 60
        }
      }]);
    }
  };

  // Check if form is valid (all weights are between 1-100)
  const isFormValid = useMemo(() => {
    if (!selectedTest || weightages.length === 0) return false;
    
    return weightages.every(w => {
      const weight = typeof w.weight === 'string' ? parseInt(w.weight) : w.weight;
      return weight && weight > 0 && weight <= 100;
    });
  }, [selectedTest, weightages]);



  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (modalMode === 'add' && !selectedTest) {
      alert('Please select a test');
      return;
    }

    try {
      // Filter out weightages with 0 or empty values
      const validWeightages = weightages.filter(w => {
        const weight = typeof w.weight === 'string' ? parseInt(w.weight) : w.weight;
        return weight && weight > 0;
      });

      // Prepare the updated test data
      const updatedTestData = {
        ...selectedTest,
        health_score_weightage: validWeightages
      };

      // Call the update API
      await updateTestMaster(selectedTest.id, updatedTestData);
      
      // Close modal after successful update
      closeModal();
    } catch (error) {
      console.error('Error updating test master:', error);
      alert('Failed to update test master. Please try again.');
      
      // Close modal even on error to prevent stuck state
      closeModal();
    }
  };

  // Select a test in "add" mode
  const selectTest = (test) => {
    setSelectedTest(test);
    const existingWeightages = test.health_score_weightage || [];
    
    // Check if the selected category is already in the weightages
    const hasSelectedCategory = existingWeightages.some(w => w.name === selectedCategory);
    
    if (hasSelectedCategory) {
      // If category exists, use existing weightages
      setWeightages(existingWeightages);
    } else {
      // If category doesn't exist, add it with weight 0 and default score levels
      setWeightages([...existingWeightages, { 
        name: selectedCategory, 
        weight: 0,
        score_levels: {
          optimal: 100,
          normal: 80,
          suboptimal: 60
        }
      }]);
    }
    
    setSearchTerm('');
  };

  // Expose functions to parent component via window object (temporary solution)
  useEffect(() => {
    window.openCategoryScoreEditModal = openEditModal;
    window.openCategoryScoreAddModal = openAddModal;
    
    return () => {
      delete window.openCategoryScoreEditModal;
      delete window.openCategoryScoreAddModal;
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`${categoryConfig.headerColor} text-white px-6 py-4 flex justify-between items-center`}>
          <h2 className="text-xl font-bold">
            {modalMode === 'edit' ? 'Edit Category Weightage' : `Add Test to ${selectedCategory}`}
          </h2>
          <button
            onClick={closeModal}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit}>
            {/* Add Mode: Test Selection */}
            {modalMode === 'add' && !selectedTest && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search and Select Test
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by biomarker name or test code..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                
                {searchTerm && (
                  <div className="mt-2 border border-gray-300 rounded-lg max-h-60 overflow-y-auto">
                    {filteredTests.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">No tests found</div>
                    ) : (
                      filteredTests.map(test => (
                        <div
                          key={test.id}
                          onClick={() => selectTest(test)}
                          className="p-3 hover:bg-orange-50 cursor-pointer border-b border-gray-200 last:border-b-0 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-1 rounded">{test.test_code}</span>
                            <span className="font-semibold text-gray-800">{test.biomarker_name}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Selected Test Info */}
            {selectedTest && (
              <div className={`mb-6 p-4 ${categoryConfig.color} rounded-lg border-l-4 ${categoryConfig.textColor.replace('text-', 'border-')}`}>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Selected Test</h3>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono text-gray-500 bg-white px-2 py-1 rounded">{selectedTest.test_code}</span>
                  <span className="font-semibold text-gray-800">{selectedTest.biomarker_name}</span>
                </div>
              </div>
            )}

            {/* Weightages Section */}
            {selectedTest && (
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700">Health Score Weightages</h3>
                    <p className="text-xs text-gray-500 mt-1">Edit the weight or remove categories as needed</p>
                  </div>
                  <button
                    type="button"
                    onClick={addWeightage}
                    className={`px-3 py-1 text-white rounded-lg transition-colors flex items-center gap-1 text-sm ${categoryConfig.buttonColor}`}
                    disabled={weightages.length >= HEALTH_SCORE_CATEGORIES.length}
                  >
                    <FaPlus size={12} />
                    Add Category
                  </button>
                </div>

                {weightages.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No weightages assigned to this test.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {weightages.map((weightage, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg bg-gray-50">
                        <div className="p-2 flex items-end gap-3">
                          <div className="w-1/2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                            <select
                              value={weightage.name}
                              onChange={(e) => {
                                const newWeightages = [...weightages];
                                newWeightages[index].name = e.target.value;
                                setWeightages(newWeightages);
                              }}
                              className={`w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                                weightage.name === selectedCategory 
                                  ? `${categoryConfig.textColor.replace('text-', 'border-')} ${categoryConfig.color} font-semibold` 
                                  : 'border-gray-300 bg-white'
                              }`}
                            >
                              {HEALTH_SCORE_CATEGORIES.map(cat => (
                                <option key={cat.name} value={cat.name} disabled={weightages.some((w, i) => i !== index && w.name === cat.name)}>
                                  {cat.name} {cat.name === selectedCategory ? '★' : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="w-1/2 flex items-end gap-2">
                            <div className="flex-1">
                              <label className="block text-xs font-medium text-gray-700 mb-1">Weight</label>
                              <input
                                type="number"
                                value={weightage.weight}
                                onChange={(e) => handleWeightChange(weightage.name, e.target.value)}
                                onBlur={(e) => {
                                  if (e.target.value === '' || e.target.value === null) {
                                    handleWeightChange(weightage.name, '0');
                                  }
                                }}
                                placeholder="Wt"
                                min="0"
                                max="100"
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                              className="px-3 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors text-sm font-medium whitespace-nowrap"
                              title="More options"
                            >
                              {expandedIndex === index ? 'Less' : 'More'}
                            </button>
                            <button
                              type="button"
                              onClick={() => removeWeightage(weightage.name)}
                              className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm font-medium flex items-center justify-center min-w-max"
                              title="Remove category"
                            >
                              <FaTrash size={14} />
                            </button>
                          </div>
                        </div>

                        {expandedIndex === index && (
                          <div className="border-t border-gray-200 p-2 bg-white">
                            <div className="flex gap-3">
                              <div className="w-1/2">
                                <label className="block text-xs font-medium text-gray-700 mb-2">Test Types</label>
                                <div className="flex gap-3 px-2 py-2 border border-gray-300 rounded bg-gray-50">
                                  {['PRO', 'BASIC', 'OTHER'].map((testType) => (
                                    <label key={testType} className="flex items-center text-xs">
                                      <input
                                        type="checkbox"
                                        checked={(weightage.associated_test_types || []).includes(testType)}
                                        onChange={(e) => {
                                          const newWeightages = [...weightages];
                                          const currentTypes = weightage.associated_test_types || [];
                                          if (e.target.checked) {
                                            newWeightages[index] = {
                                              ...newWeightages[index],
                                              associated_test_types: [...currentTypes, testType]
                                            };
                                          } else {
                                            newWeightages[index] = {
                                              ...newWeightages[index],
                                              associated_test_types: currentTypes.filter(t => t !== testType)
                                            };
                                          }
                                          setWeightages(newWeightages);
                                        }}
                                        className="w-5 h-5 border border-gray-300 rounded mr-1 cursor-pointer"
                                      />
                                      {testType}
                                    </label>
                                  ))}
                                </div>
                              </div>

                              <div className="w-1/2">
                                <div className="grid grid-cols-3 gap-2">
                                  <div>
                                    <label className="block text-xs text-green-700 font-medium mb-1">Optimal</label>
                                    <input
                                      type="number"
                                      value={weightage.score_levels?.optimal || 100}
                                      onChange={(e) => {
                                        const newWeightages = [...weightages];
                                        newWeightages[index].score_levels = {
                                          ...newWeightages[index].score_levels,
                                          optimal: parseInt(e.target.value) || 0
                                        };
                                        setWeightages(newWeightages);
                                      }}
                                      min="0"
                                      max="100"
                                      className="w-full px-2 py-2 border border-green-300 rounded text-sm text-center bg-green-50 font-medium"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-blue-700 font-medium mb-1">Normal</label>
                                    <input
                                      type="number"
                                      value={weightage.score_levels?.normal || 80}
                                      onChange={(e) => {
                                        const newWeightages = [...weightages];
                                        newWeightages[index].score_levels = {
                                          ...newWeightages[index].score_levels,
                                          normal: parseInt(e.target.value) || 0
                                        };
                                        setWeightages(newWeightages);
                                      }}
                                      min="0"
                                      max="100"
                                      className="w-full px-2 py-2 border border-blue-300 rounded text-sm text-center bg-blue-50 font-medium"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-orange-700 font-medium mb-1">Sub Optimal</label>
                                    <input
                                      type="number"
                                      value={weightage.score_levels?.suboptimal || 60}
                                      onChange={(e) => {
                                        const newWeightages = [...weightages];
                                        newWeightages[index].score_levels = {
                                          ...newWeightages[index].score_levels,
                                          suboptimal: parseInt(e.target.value) || 0
                                        };
                                        setWeightages(newWeightages);
                                      }}
                                      min="0"
                                      max="100"
                                      className="w-full px-2 py-2 border border-orange-300 rounded text-sm text-center bg-orange-50 font-medium"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

              </div>
            )}

            {/* Action Buttons */}
            {selectedTest && (
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !isFormValid}
                  className={`px-6 py-2 text-white rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed ${!isFormValid && !loading ? 'opacity-50' : categoryConfig.buttonColor}`}
                  title={!isFormValid ? 'All weights must be between 1 and 100' : ''}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default CategoryScoreModal;
